import React from "react";

import ProductFitment from "./ProductFitment";

export interface ProductFitmentTabProps {
  productId: string;
  /** True while the product page's Fitment tab is the active one. */
  active: boolean;
}

/**
 * The Fitment tab's body. It mounts the first time the tab is opened and then
 * stays mounted, hidden while another tab is active, so switching tabs never
 * drops a card's unsaved edits (every other product tab unmounts; none of
 * them holds state of its own the way the fitment cards do). The product page
 * also keys it by product id: the product route reuses the page, and a card
 * resets only when its saved row's content changes, so without the key a
 * card's staged edits on product A would survive onto product B (whose row
 * can have the same content) and B's Save would write them.
 */
export const ProductFitmentTab = ({ productId, active }: ProductFitmentTabProps) => {
  const [opened, setOpened] = React.useState(active);

  React.useEffect(() => {
    if (active) {
      setOpened(true);
    }
  }, [active]);

  if (!opened && !active) {
    return null;
  }

  return (
    // `hidden` plus an inline display so no stylesheet can show it again.
    <div
      hidden={!active}
      style={active ? undefined : { display: "none" }}
      data-test-id="product-fitment-tab"
    >
      {/* Keyed so a different product never inherits a card's unsaved edits. */}
      <ProductFitment key={productId} productId={productId} />
    </div>
  );
};

export default ProductFitmentTab;
